import { useCallback, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
    mediaLibraryApi,
    type IMediaAsset,
    type MediaProfile,
} from '@/api/services/media-library.api';
import {
    canonicalMediaType,
    isTimeBasedMedia,
    MMS_MAX_MEDIA_BYTES,
    MMS_MAX_MEDIA_KB,
    unsupportedMediaHint,
} from '@/components/shared/media/media-constants';
import { handleMutationError } from '@/lib/handle-mutation-error';

/** Client-side pre-check only — the backend re-validates by sniffing magic bytes. */
export const ALLOWED_MEDIA_MIME_TYPES: readonly string[] = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
];
export const MAX_MEDIA_ASSET_SIZE_MB = 5;
export const MAX_MEDIA_ASSET_SIZE_BYTES = MAX_MEDIA_ASSET_SIZE_MB * 1024 * 1024;
export const MEDIA_ASSET_ACCEPT = ALLOWED_MEDIA_MIME_TYPES.join(',');

/**
 * WhatsApp template headers (and carousel cards). Mirrors backend
 * `WHATSAPP_HEADER_MIME_TYPES` / `WHATSAPP_HEADER_MAX_BYTES_BY_TYPE` — Meta's
 * own per-format ceilings, so an oversize file is refused here rather than at
 * submission, where it would come back as a template rejection.
 *
 * Only what Meta takes both as an approval sample AND in a template send: its
 * sample upload accepts JPG, PNG, MP4 and PDF alone, and template documents
 * are PDF-only. See `WHATSAPP_TEMPLATE_HEADER_MEDIA_TYPES` for the per-format
 * split.
 */
export const WHATSAPP_HEADER_MIME_TYPES: readonly string[] = [
    'image/jpeg',
    'image/png',
    'video/mp4',
    'application/pdf',
];

/**
 * Extension → content type, consulted when `File.type` is empty or reports
 * something outside the list above (`image/pjpeg` for a progressive `.jpg`,
 * say). The extension is what the user chose, and the server re-derives the
 * real type from the bytes either way — so trusting it here only decides
 * whether the upload is allowed to START.
 */
const WHATSAPP_HEADER_EXTENSION_TO_MIME: Readonly<Record<string, string>> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    mp4: 'video/mp4',
    pdf: 'application/pdf',
};

/**
 * Formats people reach for that Meta will not take in a template, grouped by
 * what to convert them TO. Turns "not supported" into a next step, the same
 * idea as MMS's `unsupportedMediaHint`.
 */
type WhatsAppHeaderConversion = 'pdf' | 'mp4' | 'jpgPng';

const WHATSAPP_HEADER_CONVERSIONS: Readonly<Record<string, WhatsAppHeaderConversion>> = {
    doc: 'pdf',
    docx: 'pdf',
    xls: 'pdf',
    xlsx: 'pdf',
    ppt: 'pdf',
    pptx: 'pdf',
    txt: 'pdf',
    rtf: 'pdf',
    csv: 'pdf',
    odt: 'pdf',
    ods: 'pdf',
    odp: 'pdf',
    pages: 'pdf',
    numbers: 'pdf',
    key: 'pdf',
    '3gp': 'mp4',
    mov: 'mp4',
    avi: 'mp4',
    mkv: 'mp4',
    webm: 'mp4',
    m4v: 'mp4',
    mpeg: 'mp4',
    mpg: 'mp4',
    wmv: 'mp4',
    webp: 'jpgPng',
    gif: 'jpgPng',
    heic: 'jpgPng',
    heif: 'jpgPng',
    bmp: 'jpgPng',
    svg: 'jpgPng',
    tif: 'jpgPng',
    tiff: 'jpgPng',
};

function fileExtension(file: File): string {
    const dot = file.name.lastIndexOf('.');
    return dot > 0 ? file.name.slice(dot + 1).toLowerCase() : '';
}

/** Canonical header content type for a picked file, or null when unsupported. */
export function whatsAppHeaderMediaType(file: File): string | null {
    if (WHATSAPP_HEADER_MIME_TYPES.includes(file.type)) return file.type;
    return WHATSAPP_HEADER_EXTENSION_TO_MIME[fileExtension(file)] ?? null;
}

export const WHATSAPP_HEADER_ACCEPT = WHATSAPP_HEADER_MIME_TYPES.join(',');
export const WHATSAPP_HEADER_MAX_BYTES: Readonly<Record<string, number>> = {
    'image/jpeg': 5 * 1024 * 1024,
    'image/png': 5 * 1024 * 1024,
    'video/mp4': 16 * 1024 * 1024,
    'application/pdf': 100 * 1024 * 1024,
};

export type MediaUploadStatus = 'idle' | 'uploading' | 'error';

interface IUseMediaUploadResult {
    readonly status: MediaUploadStatus;
    readonly progress: number;
    /** Validates → presigns → PUTs → confirms. Returns `null` if validation fails or the request errors (toasted either way). */
    readonly upload: (file: File) => Promise<IMediaAsset | null>;
    readonly reset: () => void;
}

export interface IUseMediaUploadOptions {
    /**
     * Which server profile to upload under. Defaults to `IMAGE_LIBRARY`, so
     * every caller written before MMS existed keeps its 5 MB / four-image-type
     * rules untouched.
     */
    readonly profile?: MediaProfile;
}

/** Reads an image file's natural pixel dimensions without attaching it to the DOM. */
async function readImageDimensions(file: File): Promise<{ width?: number; height?: number }> {
    try {
        const bitmap = await createImageBitmap(file);
        const dims = { width: bitmap.width, height: bitmap.height };
        bitmap.close();
        return dims;
    } catch {
        // Dimension probing is best-effort — confirm() tolerates missing width/height.
        return {};
    }
}

/**
 * Validates → presigns → PUTs the file to storage → confirms, returning the
 * created `IMediaAsset`. Mirrors the presigned-PUT pattern in
 * `frontend/src/features/inbox/hooks/useNoteAttachmentUpload.ts`, scoped to a
 * single active upload since every caller uploads one image at a time (the
 * media-library dialog, the lead-form design step).
 *
 * Lives in `src/hooks/` rather than inside a feature because it is shared
 * across features and is API orchestration, not a UI pattern: the presign →
 * PUT → confirm sequence and the size/MIME allow-list must not drift between
 * callers. `confirm()` is what mints the DURABLE PUBLIC `publicUrl` — the
 * reason this path, and not a private presigned upload, is the only correct
 * one for an image rendered on an anonymous page.
 */
export function useMediaUpload(options: IUseMediaUploadOptions = {}): IUseMediaUploadResult {
    const { profile = 'IMAGE_LIBRARY' } = options;
    const { t } = useTranslation();
    const [status, setStatus] = useState<MediaUploadStatus>('idle');
    const [progress, setProgress] = useState(0);

    const reset = useCallback(() => {
        setStatus('idle');
        setProgress(0);
    }, []);

    const upload = useCallback(
        async (file: File): Promise<IMediaAsset | null> => {
            const isMms = profile === 'MMS';
            const isWhatsAppHeader = profile === 'WHATSAPP_HEADER';

            // MMS derives the content type from the file EXTENSION rather than
            // `File.type`: browsers report `.wav` as `audio/wav` while Twilio
            // accepts only `audio/vnd.wave`, and give `.amr`/`.3gp`/`.vcf`/
            // `.ics` no type at all. This value signs the PUT and becomes the
            // Content-Type we later serve, so it must match exactly.
            const resolvedType = isMms
                ? canonicalMediaType(file)
                : isWhatsAppHeader
                  ? whatsAppHeaderMediaType(file)
                  : file.type;

            if (isMms && !resolvedType) {
                const hint = unsupportedMediaHint(file);
                toast.error(
                    hint
                        ? t(
                              'media.upload.typeNotAllowedWithHint',
                              '"{{name}}" cannot be sent as MMS. Use {{alternative}} instead.',
                              { name: file.name, alternative: hint },
                          )
                        : t(
                              'media.upload.typeNotAllowedMms',
                              '"{{name}}" is not a supported MMS attachment type.',
                              { name: file.name },
                          ),
                );
                return null;
            }
            if (isWhatsAppHeader && !resolvedType) {
                // Say what to convert TO: "not supported" alone sends people
                // back to the same Word file, since WhatsApp itself sends Word
                // files happily outside templates.
                const conversion = WHATSAPP_HEADER_CONVERSIONS[fileExtension(file)];
                const params = { name: file.name };
                toast.error(
                    conversion === 'pdf'
                        ? t(
                              'media.upload.whatsAppPdfOnly',
                              'WhatsApp templates only take PDF documents. Save "{{name}}" as a PDF and upload that.',
                              params,
                          )
                        : conversion === 'mp4'
                          ? t(
                                'media.upload.whatsAppMp4Only',
                                'WhatsApp templates only take MP4 video. Convert "{{name}}" to MP4 and upload that.',
                                params,
                            )
                          : conversion === 'jpgPng'
                            ? t(
                                  'media.upload.whatsAppJpgPngOnly',
                                  'WhatsApp templates only take JPG or PNG images. Save "{{name}}" as a JPG or PNG and upload that.',
                                  params,
                              )
                            : t(
                                  'media.upload.typeNotAllowedWhatsApp',
                                  '"{{name}}" is not a supported WhatsApp header (JPG, PNG, MP4, or PDF).',
                                  params,
                              ),
                );
                return null;
            }
            if (!isMms && !isWhatsAppHeader && !ALLOWED_MEDIA_MIME_TYPES.includes(file.type)) {
                toast.error(
                    t(
                        'media.upload.typeNotAllowed',
                        '"{{name}}" is not a supported image type (PNG, JPEG, GIF, or WEBP).',
                        { name: file.name },
                    ),
                );
                return null;
            }

            // Both guards above have run, so this is a string either way.
            const contentType = resolvedType ?? file.type;

            const maxBytes = isWhatsAppHeader
                ? (WHATSAPP_HEADER_MAX_BYTES[resolvedType ?? file.type] ??
                  MAX_MEDIA_ASSET_SIZE_BYTES)
                : isMms
                  ? MMS_MAX_MEDIA_BYTES
                  : MAX_MEDIA_ASSET_SIZE_BYTES;
            if (file.size > maxBytes) {
                if (isWhatsAppHeader) {
                    toast.error(
                        t('media.upload.tooLarge', '"{{name}}" exceeds the {{size}} MB limit.', {
                            name: file.name,
                            size: Math.round(maxBytes / (1024 * 1024)),
                        }),
                    );
                } else if (isMms) {
                    toast.error(
                        isTimeBasedMedia(file)
                            ? t(
                                  'media.upload.tooLargeTimeBased',
                                  '"{{name}}" exceeds the {{size}} KB carrier limit — try a shorter clip.',
                                  { name: file.name, size: MMS_MAX_MEDIA_KB },
                              )
                            : t(
                                  'media.upload.tooLargeKb',
                                  '"{{name}}" exceeds the {{size}} KB carrier limit.',
                                  { name: file.name, size: MMS_MAX_MEDIA_KB },
                              ),
                    );
                } else {
                    toast.error(
                        t('media.upload.tooLarge', '"{{name}}" exceeds the {{size}} MB limit.', {
                            name: file.name,
                            size: MAX_MEDIA_ASSET_SIZE_MB,
                        }),
                    );
                }
                return null;
            }

            setStatus('uploading');
            setProgress(0);

            try {
                const { uploadUrl, storageKey } = await mediaLibraryApi.presign({
                    filename: file.name,
                    contentType,
                    sizeBytes: file.size,
                    profile,
                });

                await axios.put(uploadUrl, file, {
                    headers: { 'Content-Type': contentType },
                    onUploadProgress: (e) => {
                        setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0);
                    },
                });

                // Only the image library lays out against intrinsic dimensions,
                // and only an image has any — probing a PDF or an MP3 would
                // just cost a failed decode.
                const { width, height } = file.type.startsWith('image/')
                    ? await readImageDimensions(file)
                    : {};

                const asset = await mediaLibraryApi.confirm({
                    storageKey,
                    filename: file.name,
                    width,
                    height,
                });

                setStatus('idle');
                setProgress(100);
                return asset;
            } catch (error) {
                setStatus('error');
                // The server says WHY — a type or size it refused, a storage
                // plan limit — and a bare "Failed to upload" hid every one of
                // those. Anything without a readable reason (a storage PUT, a
                // dropped connection) still gets the generic line.
                handleMutationError(
                    error,
                    t('media.upload.failed', 'Failed to upload "{{name}}".', {
                        name: file.name,
                    }),
                );
                return null;
            }
        },
        [t, profile],
    );

    return { status, progress, upload, reset };
}
